'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { KonamiState } from '@/types';

const KONAMI_CODE = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
  'KeyB', 'KeyA'
];

export const useKonamiCode = () => {
  const [state, setState] = useState<KonamiState>({
    isActive: false,
    sequenceIndex: 0,
    isEasterEggRevealed: false
  });

  const [showDialog, setShowDialog] = useState(false);
  const sequenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceStartTimeRef = useRef<number | null>(null);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use refs for values that need to persist and be accessed in event handlers
  const activationCountRef = useRef(0);
  const isActivatedRef = useRef(false);
  const [activationCount, setActivationCount] = useState(0);

  // Sync activation count for UI updates
  const updateActivationCount = useCallback((increment: boolean = false) => {
    if (increment) {
      activationCountRef.current += 1;
    }
    setActivationCount(activationCountRef.current);
    return activationCountRef.current;
  }, []);

  // Get easter egg content based on activation count
  const getEasterEggContent = useCallback(() => {
    const currentCount = activationCountRef.current;
    const messages = [
      { // First time
        title: '🎮 KONAMI CODE ACTIVATED! 🎮',
        stats: [
          '🔥 Secret SRE Stats:',
          '☕ Coffee Consumed: 9,999+ cups',
          '🚨 Incidents Resolved: 42,069',
          '😴 Hours of Sleep: 3.14159',
          '🎯 Uptime Achieved: 99.999%',
          '🤖 Alerts Silenced: Over 9000!',
          '🎊 You found the hidden SRE achievement!'
        ]
      },
      { // Second time
        title: '🎮 KONAMI CODE AGAIN! 🎮',
        stats: [
          '🤖 Advanced SRE Metrics:',
          '📊 Dashboards Created: 404 (not found)',
          '🔧 Scripts Written: ∞',
          '⚡ Caffeine Level: MAXIMUM',
          '🎯 Alert Fatigue: Critical',
          '🦄 Perfect Deploys: 1 (mythical)',
          '🎊 You\'re persistent! Nice!'
        ]
      },
      { // Third+ times
        title: '🎮 KONAMI MASTER! 🎮',
        stats: [
          '🏆 Elite SRE Status:',
          `🎮 Konami Activations: ${currentCount}`,
          '🔥 SRE Level: LEGENDARY',
          '☕ Coffee Dependency: TRUE',
          '📱 On-call Hours: 24/7/365',
          '🎯 Fun Level: MAXIMUM',
          '🚀 You\'ve mastered the ancient art!'
        ]
      }
    ];

    if (currentCount === 1) return messages[0];
    if (currentCount === 2) return messages[1];
    return messages[2];
  }, []);

  // Handle key press in sequence
  const handleKeyPress = useCallback((key: string) => {
    setState(prev => {
      const expectedKey = KONAMI_CODE[prev.sequenceIndex];

      if (key === expectedKey) {
        const newIndex = prev.sequenceIndex + 1;

        // Start timing on first key
        if (newIndex === 1) {
          sequenceStartTimeRef.current = Date.now();
        }

        // Reset timeout
        if (sequenceTimeoutRef.current) {
          clearTimeout(sequenceTimeoutRef.current);
        }

        // Set new timeout for sequence completion
        sequenceTimeoutRef.current = setTimeout(() => {
          setState(prevState => ({
            ...prevState,
            sequenceIndex: 0,
            isActive: false
          }));
          sequenceStartTimeRef.current = null;
          if (sequenceTimeoutRef.current) {
            clearTimeout(sequenceTimeoutRef.current);
            sequenceTimeoutRef.current = null;
          }
        }, 3000);

        // Check if sequence is complete
        if (newIndex === KONAMI_CODE.length) {
          // Prevent spam activation (2 second cooldown)
          if (isActivatedRef.current) return prev;

          isActivatedRef.current = true;

          // Reset activation flag after cooldown
          setTimeout(() => {
            isActivatedRef.current = false;
          }, 2000);

          // Clear timeout
          if (sequenceTimeoutRef.current) {
            clearTimeout(sequenceTimeoutRef.current);
            sequenceTimeoutRef.current = null;
          }

          // Increment activation counter
          const newCount = updateActivationCount(true);

          // Console celebration with correct count
          if (newCount === 1) {
            console.log('🎮 KONAMI CODE ACTIVATED! Welcome to the secret SRE club! 🎉');
            console.log('☕ Extra caffeine boost unlocked!');
            console.log('🔥 Hidden monitoring powers revealed!');
          } else if (newCount === 2) {
            console.log('🎮 KONAMI CODE AGAIN! You\'re getting good at this! 🚀');
            console.log('📊 Advanced SRE metrics unlocked!');
            console.log('⚡ Caffeine levels reaching critical mass!');
          } else {
            console.log(`🎮 KONAMI MASTER! Activation #${newCount} achieved! 🏆`);
            console.log('🦄 You\'ve reached legendary SRE status!');
            console.log('🎯 The ancient art of reliability has been mastered!');
          }

          setShowDialog(true);

          // Auto-close after 10 seconds
          autoCloseTimeoutRef.current = setTimeout(() => {
            setShowDialog(false);
          }, 10000);

          return {
            isActive: false,
            sequenceIndex: 0,
            isEasterEggRevealed: true
          };
        }

        return {
          ...prev,
          isActive: true,
          sequenceIndex: newIndex
        };
      } else {
        // Wrong key, reset sequence
        setState(prevState => ({
          ...prevState,
          sequenceIndex: 0,
          isActive: false
        }));
        sequenceStartTimeRef.current = null;

        if (sequenceTimeoutRef.current) {
          clearTimeout(sequenceTimeoutRef.current);
          sequenceTimeoutRef.current = null;
        }

        return {
          ...prev,
          sequenceIndex: 0,
          isActive: false
        };
      }
    });
  }, [updateActivationCount]);

  // Progress calculation
  const getProgress = useCallback(() => {
    return (state.sequenceIndex / KONAMI_CODE.length) * 100;
  }, [state.sequenceIndex]);

  // Get current hint
  const getCurrentHint = useCallback(() => {
    if (state.sequenceIndex === 0) return 'Try pressing the arrow keys...';
    if (state.sequenceIndex < 4) return 'Keep going with the arrows...';
    if (state.sequenceIndex < 8) return 'More arrows...';
    if (state.sequenceIndex === 8) return 'Press B...';
    if (state.sequenceIndex === 9) return 'Press A to complete!';
    return '';
  }, [state.sequenceIndex]);

  // Close dialog
  const closeDialog = useCallback(() => {
    setShowDialog(false);
    // Clear auto-close timer
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    // Reset activation immediately when closed manually
    isActivatedRef.current = false;
  }, []);

  // Store latest handlers in refs to avoid stale closures
  const handleKeyPressRef = useRef(handleKeyPress);
  const closeDialogRef = useRef(closeDialog);
  const showDialogRef = useRef(showDialog);

  // Update refs when values change
  handleKeyPressRef.current = handleKeyPress;
  closeDialogRef.current = closeDialog;
  showDialogRef.current = showDialog;

  // Keyboard event listener - STABLE IMPLEMENTATION
  useEffect(() => {
    // Create a stable event handler that doesn't change
    const stableHandler = (event: KeyboardEvent) => {
      // Handle ESC key to close dialog
      if (event.key === 'Escape' && showDialogRef.current) {
        closeDialogRef.current();
        return;
      }

      // Prevent default behavior for arrow keys to avoid page scrolling
      if (event.key.startsWith('Arrow')) {
        event.preventDefault();
      }

      // Use the ref to call the latest handler
      handleKeyPressRef.current(event.code);
    };

    // Add event listener only once
    document.addEventListener('keydown', stableHandler);

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', stableHandler);

      // Clean up timers
      if (sequenceTimeoutRef.current) {
        clearTimeout(sequenceTimeoutRef.current);
        sequenceTimeoutRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Reset easter egg
  const resetEasterEgg = useCallback(() => {
    setState({
      isActive: false,
      sequenceIndex: 0,
      isEasterEggRevealed: false
    });
    setShowDialog(false);
    isActivatedRef.current = false;

    // Reset the activation count ref and state
    activationCountRef.current = 0;
    setActivationCount(0);

    // Clear timers
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    if (sequenceTimeoutRef.current) {
      clearTimeout(sequenceTimeoutRef.current);
      sequenceTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, []);

  return {
    state,
    showDialog,
    activationCount,
    getProgress,
    getCurrentHint,
    closeDialog,
    resetEasterEgg,
    getEasterEggContent,
    isSequenceActive: state.isActive,
    sequenceProgress: getProgress()
  };
};
